import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { Router } from '@angular/router';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { AuthGuard } from 'src/app/shared/auth.service'

@Component({
  selector: 'app-agent-setup',
  templateUrl: './agent-setup.component.html',
  styleUrls: ['./agent-setup.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Agent settings page — groups the previously-scattered global "agent admin"
 * surfaces that used to live as three of the five accordion tabs on
 * `EditUserAccessComponent` (`/edit-access`): Команда агента (Teams),
 * Банківські облікові дані, and Заблоковані контрагенти (банк). Moved here
 * 2026-09-13 (owner) so `/edit-access` stays focused on its original purpose
 * (Keycloak/security-group user access management) instead of accumulating
 * unrelated agent-ops config. `app-edit-agent-customer-tier-tab`
 * (per-client, lives in `edit-client`) and `app-agent-spam-queue` (`/home`)
 * are deliberately NOT included — the former is client-scoped business data,
 * not a global setting, and the latter is an operational queue rather than
 * config; both are candidates for a future consolidation pass, not this one.
 *
 * Each tab keeps the exact gating it already had on `/edit-access`, unchanged
 * by this move: `canAccessAdminTabs` (portal-admin-equivalent, the original
 * four-condition admin bar) still gates Команда агента and Банківські
 * облікові дані; `canManageBlockedCounterparties` (portal-admin OR
 * finance-manager OR can-manage-payments) still gates only the blocked-
 * counterparties tab. Same *ngIf-not-just-[disabled] pattern as before
 * (see that component's own header comment for why [disabled] alone is not
 * enough — Angular content projection instantiates a tab's child component,
 * and its ngOnInit(), regardless of the parent p-accordionTab's
 * disabled/selected state).
 *
 * **Рівні клієнтів (винятки)** (`app-edit-agent-customer-tier-list-tab`,
 * added 2026-09-23) joined this page the same way, gated by
 * `canAccessAdminTabs` for the same reason as Команда агента/Банківські
 * облікові дані — the admin-wide exceptions list is IT/infra config, not a
 * finance-team tool, so it does NOT use `canManageBlockedCounterparties`.
 * See `docs/PROMPT_agent-customer-tier-admin-list.md`.
 */
export class AgentSetupComponent implements OnInit {

  public userAccessGroup: number;

  /** Same admin bar `EditUserAccessComponent` still uses for its own
   *  remaining tabs — unchanged by this move. Gates Команда агента and
   *  Банківські облікові дані here. */
  public canAccessAdminTabs: boolean = false;

  /** `portal-admin` OR `finance-manager` OR `can-manage-payments` — gates
   *  only Заблоковані контрагенти (банк), independent of `canAccessAdminTabs`
   *  the same way it did on `/edit-access`. */
  public canManageBlockedCounterparties: boolean = false;

  windowsTitle: string = 'Налаштування агента';

  constructor(
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
    private router: Router,
    private authGuard: AuthGuard
  ) {
  }

  /** Guards access and sets the browser title. */
  ngOnInit(): void {
    this.globalsettingsservice.getGlobalSettings();

    this.userAccessGroup = this.globalsettingsservice.getAccessGroup();

    // Same two independent gates EditUserAccessComponent used to compute for
    // these same three tabs (SEC-SGM-ROLE-1 for the admin bar; owner,
    // 2026-09-13, for the finance OR) -- unchanged by relocating them here.
    const requiredAccessGroup = 10000000;
    this.canAccessAdminTabs =
      (this.userAccessGroup >= requiredAccessGroup)
      && this.authGuard.canManageKeycloakUsers()
      && this.authGuard.canManageJSreportUsers()
      && this.globalsettingsservice.hasSomeRoles('portal-admin');

    this.canManageBlockedCounterparties =
      this.globalsettingsservice.hasSomeRoles('portal-admin', 'finance-manager', 'can-manage-payments');

    if (!this.canAccessAdminTabs && !this.canManageBlockedCounterparties) {
      alert('У вас недостаточно прав для доступа к этой странице.');
      this.router.navigate(['/']);
      return;
    }

    this.titleService.setTitle(this.windowsTitle);
  }
}
